import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { useRpmPerState } from "@/components/home/useRpmPerState";
import { useMapboxKey } from "@/components/maps/useMapboxKey";
import { Cities } from "@/components/home/cities";
import { addCity, fitBounds, removeCities } from "@/utils/map";
import { ErrorComponent } from "@/components/common/errors";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { API } from "@/services/API";
import { getMarketCenters, getMarkets, getUsaStates } from "@/state/mapDataSlice";

export const HomepageMap = ({ hasError = false, setHasError = (_) => {}, outboundPerMarket }) => {
    const { markets, marketCenters, states } = useSelector((state) => state.mapData);
    const mapContainer = useRef(null);
    const mapRef = useRef(null);
    const [mapIsLoaded, setMapIsLoaded] = useState(false);
    const [nationalAverageRPM, setNationalAverageRPM] = useState(null);

    const mapboxAccessKey = useMapboxKey(true);
    const { getColor } = useRpmPerState();
    const profile = useSelector((state) => state.profile);
    const isDarkTheme = useIsDarkTheme();
    const dispatch = useDispatch();

    const initialBounds = [
        [-126, 50.9],
        [-67.93, 23.3],
    ];
    const padding = { top: 0, bottom: 0, left: 24, right: 24 };

    useEffect(() => {
        if (!states.data) {
            dispatch(getUsaStates());
        }
        if (!markets.data) {
            dispatch(getMarkets());
        }
        if (!marketCenters.data) {
            dispatch(getMarketCenters());
        }
        API.get("/analytics/national-average-rpm", {
            params: {
                trailer: "dry_van",
                "interval-in-days": "30",
            },
        })
            .then((response) => {
                setNationalAverageRPM(response.average);
            })
            .catch(() => {
                setHasError(true);
            });
    }, []);

    useEffect(() => {
        if (
            mapContainer.current &&
            mapboxAccessKey &&
            profile.data?.webTheme &&
            outboundPerMarket &&
            Object.keys(outboundPerMarket).length > 0
        ) {
            mapboxgl.accessToken = mapboxAccessKey;
            const map = new mapboxgl.Map({
                container: mapContainer.current,
                style: "mapbox://styles/lioneightdev/cm4zv8t6b00cj01sp11qp616w",
                center: [-98.5795, 37.8283],
                dragRotate: false,
                touchZoomRotate: false,
                pitchWithRotate: false,
                dragPan: false,
                bounds: initialBounds,
                fitBoundsOptions: { padding, duration: 400 },
            });
            mapRef.current = map;

            map.on("load", async () => {
                Cities.forEach((city) => {
                    addCity(map, city);
                });

                setMapIsLoaded(true);
            });

            map.scrollZoom.disable();
            map.doubleClickZoom.disable();

            const handleFitBounds = () => {
                fitBounds(map, padding, 100);
            };

            window.addEventListener("resize", handleFitBounds);

            return () => {
                removeCities();
                map.remove();
                window.removeEventListener("resize", handleFitBounds);
            };
        }
    }, [mapboxAccessKey, profile.data?.webTheme, outboundPerMarket]);

    useEffect(() => {
        if (mapRef.current && markets.data && states.data && mapIsLoaded && nationalAverageRPM && Object.keys(outboundPerMarket).length) {
            const map = mapRef.current;
            const marketsData = {
                ...markets.data,
                features: markets.data.features.map((feature) => {
                    const avgOutbound = outboundPerMarket[feature.properties.marketName]?.averageStat.average;
                    if (!avgOutbound) {
                        console.log("No outbound value", feature.properties.marketName);
                    }
                    return {
                        ...feature,
                        properties: {
                            ...feature.properties,
                            color: getColor(avgOutbound || 0, nationalAverageRPM),
                            rpm: avgOutbound || 0,
                        },
                    };
                }),
            };
            map.addSource("markets", {
                type: "geojson",
                data: marketsData,
            });

            map.addLayer({
                id: "markets-layer",
                type: "fill",
                source: "markets",
                layout: {},
                paint: {
                    "fill-color": ["get", "color"],
                },
            });

            map.addLayer({
                id: "hover-layer",
                type: "fill",
                source: "markets",
                paint: {
                    "fill-color": isDarkTheme ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                },
                filter: ["==", "marketName", ""],
            });
            fitBounds(map, padding, 400);

            map.addSource("states", {
                type: "geojson",
                data: states.data,
            });

            map.addLayer({
                id: "states-border-layer",
                type: "line",
                source: "states",
                paint: {
                    "line-color": isDarkTheme ? "#131414" : "#66857B",
                    "line-width": 0.5,
                    "line-opacity": 0.8,
                },
            });

            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                className: "custom-popup",
                maxWidth: "auto",
            });

            const clearHover = () => {
                map.setFilter("hover-layer", ["==", "marketName", ""]);
                popup.remove();
                map.getCanvas().style.cursor = "";
            };

            // Add mouseenter event to highlight state and show popup
            map.on("mousemove", "markets-layer", (event) => {
                const features = map.queryRenderedFeatures(event.point, {
                    layers: ["markets-layer"],
                });

                if (features.length > 0) {
                    const feature = features[0];
                    const { marketName, marketState, rpm } = feature.properties;
                    map.setFilter("hover-layer", ["==", "marketName", marketName]);
                    popup
                        .setLngLat(event.lngLat)
                        .setHTML(
                            `<div class="font-sans flex flex-col rounded py-2 px-3 bg-cm-p-surface min-w-[218px] min-h-[62px] border border-cm-p-outline drop-shadow-[0_2px_4px_rgba(0,0,0,0.08)] text-sm/[22px]">
                                    <div class="text-cm-p-text-primary">${marketName}, ${marketState}</div>
                                    <div class="text-cm-p-text-primary text-sm/[22px]"><span class="font-semibold">$ ${rpm}</span> avg outbound RPM</div>
                                </div>`,
                        )
                        .addTo(map);
                } else {
                    clearHover();
                }
            });

            map.on("mouseleave", "markets-layer", clearHover);
            map.on("mouseenter", "markets-layer", () => {
                map.getCanvas().style.cursor = "pointer";
            });

            map.on("error", async (e) => {
                console.log("Map error", e);
                setHasError(true);
            });
        }
    }, [markets.data, states.data, mapIsLoaded, nationalAverageRPM, outboundPerMarket]);

    return hasError ? (
        <ErrorComponent className={"h-full min-h-[500px]"} />
    ) : (
        <div
            ref={mapContainer}
            style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "100%", height: "100%", transition: "opacity 0.4s ease-out" }}
        />
    );
};
